/**
 * TimelinePage (Phase-2, Module 10)
 *
 * Full health timeline for the logged-in officer — auto-populated from every
 * Phase-2 module. A compact version also appears on the Officer dashboard.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Chip, Stack, MenuItem, FormControl, InputLabel, Select, Card, CardContent } from '@mui/material';
import { Timeline as TimelineIcon } from '@mui/icons-material';
import { PageScaffold, OfficerSelector } from '../../components/common';
import { TimelineApi } from '../../services/phase2Api';
import type { TimelineEvent } from '../../types/dashboard';
import { useCurrentOfficer } from '../../hooks/useCurrentOfficer';
import { useNavigate, useLocation } from 'react-router-dom';

const CATEGORY_COLORS: Record<string, string> = {
  APPOINTMENT: '#3B82F6', PRESCRIPTION: '#8B5CF6', INVESTIGATION: '#EC4899',
  BLOOD_TEST: '#EF4444', ADMISSION: '#F59E0B', INSURANCE: '#10B981',
  DOCUMENT: '#6366F1', MEDICAL_REPORT: '#0EA5E9', OTHER: '#64748B',
};

const TimelinePage: React.FC = () => {
  const { officerId } = useCurrentOfficer();
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [catFilter, setCatFilter] = useState('');

  const load = useCallback(async () => {
    if (!officerId) return;
    setLoading(true); setError(null);
    try {
      const data = await TimelineApi.listByOfficer(officerId);
      const list: TimelineEvent[] = Array.isArray(data) ? data : (data?.content || []);
      setEvents(list);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load timeline'); }
    finally { setLoading(false); }
  }, [officerId]);

  useEffect(() => { load(); }, [load]);

  const categories = Array.from(new Set(events.map((e) => e.eventCategory)));
  const filtered = catFilter ? events.filter((e) => e.eventCategory === catFilter) : events;

  return (
    <PageScaffold title="Health Timeline" subtitle="Your complete health history" icon={<TimelineIcon />}
      loading={loading && events.length === 0} error={error} onDismissError={() => setError(null)}>
      {!officerId && (
        <OfficerSelector onSelect={(id) => navigate(`${location.pathname}?officerId=${id}`)} />
      )}
      {officerId && (
      <>
      <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel>Category</InputLabel>
        <Select value={catFilter} label="Category" onChange={(e) => setCatFilter(e.target.value)}>
          <MenuItem value="">All Categories</MenuItem>
          {categories.map((c) => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
        </Select>
      </FormControl>

      <Stack spacing={1.5}>
        {filtered.map((e) => {
          const color = CATEGORY_COLORS[e.eventCategory] || '#64748B';
          return (
            <Card key={e.id} variant="outlined" sx={{ borderLeft: `4px solid ${color}` }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <Chip size="small" label={e.eventCategoryDisplayName || e.eventCategory.replace(/_/g, ' ')} sx={{ bgcolor: color, color: '#fff' }} />
                  {e.statusDisplay && <Chip size="small" label={e.statusDisplay} variant="outlined" />}
                  {e.referenceNumber && <Chip size="small" label={e.referenceNumber} variant="outlined" />}
                </Box>
                <Typography variant="subtitle2">{e.eventTitle}</Typography>
                {e.eventDescription && <Typography variant="body2" color="text.secondary">{e.eventDescription}</Typography>}
                <Typography variant="caption" color="text.secondary">{e.eventDate} · {e.createdByName || 'System'}</Typography>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && !loading && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No timeline events yet.</Typography>
        )}
      </Stack>
      </>
      )}
    </PageScaffold>
  );
};

export default TimelinePage;